import { Helmet } from "react-helmet";
import { useState, useRef } from "react";
import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";
import "../styles/ContactForm.css";

function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    goal: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [otp, setOtp] = useState("");
  const [enteredOtp, setEnteredOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");

  const recaptchaRef = useRef();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateInputs = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?\d{7,15}$/;

    if (!emailRegex.test(formData.email)) {
      alert("Please enter a valid email address.");
      return false;
    }

    if (formData.phone && !phoneRegex.test(formData.phone)) {
      alert("Please enter a valid phone number.");
      return false;
    }

    if (!recaptchaToken) {
      alert("Please complete the reCAPTCHA.");
      return false;
    }

    return true;
  };

  const sendOtpEmail = () => {
    const generatedOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setOtp(generatedOtp);
    setIsSubmitting(true);

    const expiryTime = new Date(Date.now() + 15 * 60000).toLocaleTimeString(); // 15 minutes from now

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_OTP_TEMPLATE_ID,
        {
          email: formData.email, // ✅ matches template field {{email}}
          passcode: generatedOtp, // ✅ matches {{passcode}}
          time: expiryTime,       // ✅ matches {{time}} (optional)
          "g-recaptcha-response": recaptchaToken, // ✅ for reCAPTCHA validation
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setIsOtpSent(true);
        setIsSubmitting(false);
      })
      .catch((error) => {
        console.error("Failed to send OTP", error);
        alert("Failed to send OTP. Please try again.");
        setIsSubmitting(false);
      });
  };

  const sendFormData = () => {
    setIsSubmitting(true);

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_FORM_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          phone: formData.phone,
          goal: formData.goal,
          message: formData.message,
          "g-recaptcha-response": recaptchaToken,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setShowModal(true);
        setFormData({
          name: "",
          email: "",
          phone: "",
          goal: "",
          message: "",
        });
        setIsOtpSent(false);
        setEnteredOtp("");
        setIsSubmitting(false);
        recaptchaRef.current.reset();
        setRecaptchaToken("");
      })
      .catch((error) => {
        console.error("Failed to send message", error);
        alert("Failed to send message. Please try again.");
        setIsSubmitting(false);
      });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateInputs()) return;
    if (!isOtpSent) sendOtpEmail();
  };

  const goals = [
    "Weight Loss",
    "Muscle Building",
    "Strength Training",
    "Athletic Performance",
    "General Fitness",
    "Injury Rehabilitation",
    "Nutrition Guidance",
    "Other",
  ];

  return (
    <section id="contact" className="contact" itemScope itemType="https://schema.org/ContactPage">
      <Helmet>
        <title>Contact Lu - Fit | Personal Trainer in Cape Town</title>
        <meta
          name="description"
          content="Get in touch with Lu - Fit, your certified personal trainer in Cape Town. Call, email or send us a message to book a session or ask a question."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="contact-container">
        <div className="contact-content">
          <h2 className="contact-title">Ready to Start Your Transformation?</h2>
          <p className="contact-subtitle">
            Book your free 30-minute consultation today. We'll discuss your goals, assess your current fitness level,
            and create a personalized plan that fits your lifestyle.
          </p>
          <div className="contact-info" aria-label="Contact Details">
            <h3>Visit or Call Us</h3>
            <div>
              <address className="contact-item" itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                <span className="contact-info-heading">Address:</span>
                <span itemProp="streetAddress">123 Fit Street, Cape Town, Western Cape</span>
              </address>
              <div className="contact-item">
                  <span className="contact-info-heading">Phone:</span>
                  <span>
                    <a href="tel:+27211234567" aria-label="Call Lu Fit at +27 21 123 4567" itemProp="telephone">+27 21 123 4567</a>
                  </span>
              </div>
              <div className="contact-item">
                  <span className="contact-info-heading">Email:</span>
                  <span>
                    <a href="mailto:info@lu-fit.co.za" aria-label="Email Lu Fit" itemProp="email">info@lu-fit.co.za</a>
                  </span>
              </div>
            </div>

            <div className="google-maps" aria-label="Google Maps Location">
              <iframe
                title="Lu - Fit Location in Cape Town"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3302.492292395382!2d18.4232!3d-33.918861!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1dcc6762d6dc2bb9%3A0xd182f697ba01b8c6!2sCape%20Town%20CBD!5e0!3m2!1sen!2sza!4v1680012345678"
                width="300"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </div>

        <div className="form-card">
          <h3 className="form-title">Schedule Your Free Consultation</h3>
          <form onSubmit={handleSubmit} className="form-body" itemScope itemType="https://schema.org/ContactPoint">
            <div className="input-grid">
              <div>
                <label htmlFor="name">Full Name *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Your name" itemProp="name" />
              </div>
              <div>
                <label htmlFor="email">Email Address *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="your@email.com" itemProp="email" />
              </div>
            </div>

            <div className="input-grid">
              <div>
                <label htmlFor="phone">Phone Number</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="(555) 123-4567" />
              </div>
              <div>
                <label htmlFor="goal">Primary Goal</label>
                <select id="goal" name="goal" value={formData.goal} onChange={handleInputChange}>
                  <option value="">Select your goal</option>
                  {goals.map((goal) => (
                    <option key={goal} value={goal}>{goal}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="message">Tell me about your fitness goals</label>
              <textarea id="message" name="message" value={formData.message} onChange={handleInputChange} rows={4} placeholder="Share your fitness goals, current activity level, injuries, or concerns..." />
            </div>

            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={(token) => setRecaptchaToken(token)}
              className="recaptcha"
            />

            {!isOtpSent ? (
              <button type="submit" disabled={isSubmitting} className="submit-button">
                {isSubmitting ? (<><div className="spinner"></div> Sending OTP...</>) : (<>Send Verification Code</>)}
              </button>
            ) : (
              <>
                <div>
                  <label htmlFor="otp">Enter the OTP sent to your email:</label>
                  <input type="text" id="otp" value={enteredOtp} onChange={(e) => setEnteredOtp(e.target.value)} placeholder="6-digit code" />
                </div>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (enteredOtp === otp) {
                      sendFormData();
                    } else {
                      alert("Incorrect OTP. Please try again.");
                    }
                  }}
                  className="submit-button"
                >
                  {isSubmitting ? (<><div className="spinner"></div> Sending Message...</>) : (<>Verify & Send Message <img src="/media/icons/send.svg" alt="send" className="icon-right" /></>)}
                </button>
              </>
            )}
          </form>

          <nav className="contact-info-socials" aria-label="Social Media Links">
            <a href="https://x.com/lu_fit" aria-label="Twitter" target="_blank" rel="noopener noreferrer">
              <img src="/media/icons/twitter-logo.png" alt="Twitter logo" loading="lazy" />
            </a>
            <a href="https://tiktok.com/@lu_fit" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer">
              <img src="/media/icons/whatsapp-logo.png" alt="WhatsApp" loading="lazy" />
            </a>
            <a href="https://facebook.com/lu.fit" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
              <img src="/media/icons/facebook-logo.png" alt="Facebook logo" loading="lazy" />
            </a>
            <a href="https://instagram.com/lu.fit" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
              <img src="/media/icons/instagram-logo.png" alt="Instagram logo" loading="lazy" />
            </a>
          </nav>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h4>Thank you!</h4>
            <p>Your message has been sent successfully.</p>
            <button onClick={() => setShowModal(false)}>Close</button>
          </div>
        </div>
      )}
    </section>
  );
}

export default ContactForm;
